<template>
  <div class="segmentation-limiter">
    <div class="limiter-wrap" :class="{ '-active': limiter.active }">
      <v-card class="limiter-card">
        <div class="limiter-header">
          <mf-icon size="16" :color="iconColor">content_cut</mf-icon>
          <span class="limiter-title">Rankeamento e Corte</span>
          <mf-toggle v-model="limiter.active" hide-details />
        </div>

        <transition name="expand-y" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave" @after-leave="onAfterLeave">
          <div v-show="limiter.active" class="limiter-body">
            <v-divider class="my-3" />
            <div>
              <div>
                <span class="input-label">Limite de clientes no segmento</span>
                <v-text-field
                  ref="limitInput"
                  v-model="displayLimitValue"
                  class="limit-input"
                  :disabled="disabled"
                  outlined
                  dense
                  type="number"
                  :hide-details="!limitError"
                  :error-messages="limitError"
                  placeholder="Digite o limite"
                  @input="onLimitInput"
                />
                <div class="info-container">
                  <span>Tamanho original: </span>
                  <span v-if="shouldShowOriginalSize" class="segment-size">{{ originalSegmentSize | localeString }}</span>
                  <span v-else class="atualizar-segmento" @click="updateSegmentInsights">Atualizar segmento</span>
                </div>
              </div>

              <div>
                <span class="input-label">Estratégia de limitação</span>
                <v-select
                  v-model="limiter.strategy"
                  class="selector-container"
                  hide-details
                  outlined
                  dense
                  append-icon="expand_more"
                  :items="limitationStrategies"
                />
              </div>

              <div v-if="hasLimitationStrategyConfiguration">
                <div class="my-4">
                  <mf-select-units-dialog
                    template-type="text"
                    hide-details
                    outlined
                    units-label=""
                    dense
                    enable-inactive-units-selection
                    :check-all-units-on-init="!limiter.unitsIds.length"
                    :selected-units.sync="limiter.unitsIds"
                    :selected-units-configuration.sync="limiter.units"
                    :units="allUnits"
                    :tag-list="tagList"
                    :group-list="unitsGroups"
                  />
                </div>

                <div class="mb-4">
                  <modal-graphql-search-content
                    v-if="!disabled"
                    dense
                    template-type="text"
                    :placeholder="loadingProducts ? 'Carregando' : 'Todos os produtos'"
                    :selected-items.sync="limiter.products"
                    item-type="produto"
                    hide-details
                    :loading="loadingProducts"
                  />
                  <modal-view-items
                    v-else
                    buttonText="Ver produtos"
                    table-title="Lista de produtos"
                    search-placeholder="Busque um produto"
                    :headers="productViewOnlydHeaders"
                    :items="limiter.products"
                  />
                </div>
                <div>
                  <segmentation-limiter-data-select v-model="limiter.filterDate" :max-filter-days="90" dense />
                </div>
              </div>
            </div>
          </div>
        </transition>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import ModalGraphqlSearchContent from '@/components/ModalGraphqlSearchContent.vue'
import ModalViewItems from '@/components/ModalViewItems.vue'
import SegmentationLimiterDataSelect from '@/modules/clientes/segmentation/views/components/SegmentationLimiterDataSelect.vue'
import gql from 'graphql-tag'
import { required, minValue } from 'vuelidate/lib/validators'

export const QUERY_PRODUCTS = gql`
  query Products($skus: [String]) {
    products(skus: $skus) {
      products {
        _id
        sku
        ean
        eans
        name
      }
    }
  }
`

export default {
  name: 'SegmentationLimiter',
  components: {
    ModalGraphqlSearchContent,
    ModalViewItems,
    SegmentationLimiterDataSelect,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    segment: {
      type: Object,
      default: () => {},
    },
    segmentOriginalSize: {
      type: Number,
      default: null,
    },
    isSegmentationWithoutRfv: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loadingProducts: false,
    isInitialMount: true,
    limiter: {
      active: false,
      limit: null,
      strategy: 'random',
      unitsIds: [],
      units: [],
      products: [],
      filterDate: {
        field: 'date_dynamic',
        values: ['d-30<='],
      },
    },
    displayLimitValue: null,
  }),
  computed: {
    ...mapGetters(['tagList', 'unitsGroups', 'segmentationLimiter']),
    ...mapState({
      segmentationSize: (state) => state.segmentation.segmentationSize,
      persistOriginalSize: (state) => state.segmentation.persistOriginalSize,
    }),
    limitError() {
      if (this.$v.limiter?.limit?.$error) {
        if (!this.$v.limiter.limit.required) return 'Informe um limite para o segmento ou desative a limitação'
        if (!this.$v.limiter.limit.minValue) return 'O limite deve ser maior que 0'
      }

      return null
    },
    productViewOnlydHeaders() {
      return [
        {
          text: 'SKU',
          align: 'start',
          sortable: false,
          value: 'sku',
          width: '10%',
        },
        {
          text: 'EAN',
          align: 'start',
          sortable: false,
          value: 'ean',
          width: '10%',
        },
        {
          text: 'Nome',
          align: 'start',
          sortable: true,
          value: 'name',
          width: '80%',
        },
      ]
    },
    allUnits() {
      return this.$store.getters.units
    },
    limitationStrategies() {
      return [
        { text: 'Aleatória', value: 'random' },
        { text: 'Maior faturamento', value: 'most_revenue' },
        { text: 'Maior número de cupons', value: 'most_coupons' },
        { text: 'Maior ticket médio', value: 'most_average_ticket' },
        { text: 'Menor faturamento', value: 'least_revenue' },
        { text: 'Menor número de cupons', value: 'least_coupons' },
        { text: 'Menor ticket médio', value: 'least_average_ticket' },
      ]
    },
    hasLimitationStrategyConfiguration() {
      return this.limiter.strategy !== 'random'
    },
    iconColor() {
      return this.limiter.active ? '#0063C6' : '#31363B'
    },
    shouldShowOriginalSize() {
      return this.persistOriginalSize
    },
    originalSegmentSize() {
      return this.segmentOriginalSize || 0
    },
  },
  watch: {
    limiter: {
      deep: true,
      handler() {
        this.updateLimiter()
        this.$emit('active-change', this.limiter.active)
      },
    },
    segment: {
      handler(newVal, oldVal) {
        if (newVal && oldVal && newVal !== oldVal) {
          this.resetLimiterState()
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.mountLimiter()
    this.$emit('active-change', this.limiter.active)
  },
  beforeDestroy() {
    this.resetLimiterState()
  },
  methods: {
    ...mapActions([
      'updateSegmentationLimiter',
      'setSegmentationSize',
      'setPersistOriginalSize',
      'calculateSegmentSize',
      'calculateSegmentSizeAndRFVClusters',
      'setIsSegmentStale',
      'setLoadingSegmentInsights',
    ]),
    mountLimiter() {
      if (!this.segmentationLimiter) return

      this.limiter.active = this.segmentationLimiter.active
      this.limiter.strategy = this.segmentationLimiter.strategy
      this.limiter.limit = this.segmentationLimiter.limit

      // Set display value with formatting
      if (this.limiter.limit) {
        this.displayLimitValue = this.limiter.limit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }

      const unitsFilter = this.segmentationLimiter.filters.find((filter) => filter.field === 'unit')
      const productsFilter = this.segmentationLimiter.filters.find((filter) => filter.field === 'product')
      const dateFilter = this.segmentationLimiter.filters.find((filter) => filter.field === 'date_fixed' || filter.field === 'date_dynamic')
      this.limiter.unitsIds = unitsFilter ? unitsFilter.values : []
      if (dateFilter) {
        this.limiter.filterDate = dateFilter
      }

      this.setProducts(productsFilter)
    },
    updateLimiter() {
      const limiter = {
        active: this.limiter.active && this.limiter.limit !== null && this.limiter.limit !== '',
        strategy: this.limiter.strategy,
        limit: Number(this.limiter.limit || 0),
        filters: [],
      }

      if (this.limiter.strategy !== 'random' && this.limiter.products?.length > 0) {
        limiter.filters.push({
          field: 'product',
          values: this.limiter.products.map((product) => product.sku),
        })
      }

      if (this.limiter.strategy !== 'random' && this.limiter.unitsIds?.length > 0) {
        limiter.filters.push({
          field: 'unit',
          values: this.limiter.unitsIds,
        })
      }

      if (this.limiter.strategy !== 'random' && this.limiter.filterDate) {
        limiter.filters.push({
          field: this.limiter.filterDate.field,
          values: this.limiter.filterDate.values,
        })
      }

      this.updateSegmentationLimiter(limiter)
    },
    async setProducts(productsFilter) {
      if (!productsFilter?.values?.length) return

      try {
        this.loadingProducts = true
        const {
          data: {
            products: { products: productsArray },
          },
        } = await this.$apollo.query({
          query: QUERY_PRODUCTS,
          variables: {
            skus: productsFilter.values,
          },
          fetchPolicy: 'no-cache',
        })
        if (productsArray && productsArray.length) {
          this.limiter.products = productsArray
        }
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Erro ao carregar produtos, tente novamente mais tarde.', snackbarColor: 'error' })
      }
      this.loadingProducts = false
    },
    onLimitInput(value) {
      // Remove all non-digit characters
      const numericValue = value.replace(/\D/g, '')
      const number = numericValue ? Number(numericValue) : null

      // Update the actual limit
      this.limiter.limit = number
      this.displayLimitValue = number ? number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''
    },
    onEnter(el) {
      el.style.height = '0px'
      el.style.overflow = 'hidden'
      void el.offsetHeight
      el.style.height = el.scrollHeight + 'px'
    },
    onAfterEnter(el) {
      el.style.height = 'auto'
      el.style.overflow = 'visible'
    },
    onLeave(el) {
      el.style.height = el.scrollHeight + 'px'
      el.style.overflow = 'hidden'
      void el.offsetHeight
      el.style.height = '0px'
    },
    onAfterLeave(el) {
      el.style.height = null
      el.style.overflow = null
    },
    resetLimiterState() {
      this.limiter = {
        active: false,
        limit: null,
        strategy: 'random',
        unitsIds: [],
        units: [],
        products: [],
        filterDate: {
          field: 'date_dynamic',
          values: ['d-30<='],
        },
      }
      this.displayLimitValue = null
      this.isInitialMount = true
      this.setSegmentationSize(null)
      this.setPersistOriginalSize(false)
    },
    async updateSegmentInsights() {
      this.setLoadingSegmentInsights(true)
      try {
        if (this.isSegmentationWithoutRfv) {
          await this.calculateSegmentSize()
        } else {
          await this.calculateSegmentSizeAndRFVClusters()
        }
        this.setIsSegmentStale(false)
        this.setPersistOriginalSize(true)
      } catch (e) {
        this.$snackbar({ message: `Falha ao calcular insights do segmento`, snackbarColor: 'error' })
      }
      this.setLoadingSegmentInsights(false)
    },
  },
  validations() {
    if (!this.limiter.active) return {}

    return {
      limiter: {
        limit: { required, minValue: minValue(1) },
      },
    }
  },
}
</script>

<style lang="scss">
.segmentation-limiter {
  width: 300px;
  height: 40px;
  box-sizing: border-box;

  .limiter-wrap {
    width: 300px;
    position: absolute;
    right: 0px;
    top: 0;
    z-index: 8;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid $cinza-l50;
    background: $branco;

    &.-active {
      border-color: $marca-0-l20;
    }
  }
  .limiter-card {
    overflow: hidden;
    box-shadow: none !important;
  }
  .limiter-header {
    padding: 0 !important;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .limiter-body {
    padding-bottom: 15px;
  }
  .limiter-title {
    width: 100%;
    color: $cinza-d10;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.14px;
  }
  .expand-y-enter-active,
  .expand-y-leave-active {
    transition: height 200ms ease-in;
  }
  .expand-y-enter-from,
  .expand-y-leave-to {
    opacity: 0;
  }
  .input-label {
    color: $cinza-d10;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }
  .info-container {
    color: $cinza-0;
    font-size: 10px;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.167px;
    padding-top: 4px;
    padding-left: 8px;
    margin-bottom: 24px;
  }
  .segment-size {
    color: $cinza-d10;
  }
  .limit-input input {
    color: $cinza-d10;
    font-size: 14px;
    line-height: 20px;
  }
  .selector-container .v-select__selection {
    color: $cinza-d10;
    font-size: 14px;
    line-height: 20px;
  }
  .atualizar-segmento {
    color: $marca-0-l40;
    font-size: 10px;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.167px;
    text-decoration-line: underline;
    cursor: pointer;
  }
}
</style>
